import type {
    ContentDefinition,
    ForDefinition,
    ParsedScope,
    PluginDefinition,
} from "@magic-md/core";

function mapEntries<T>(value: unknown): Array<[string, T]> {
    if (value instanceof Map) {
        return Array.from(value.entries()) as Array<[string, T]>;
    }
    if (Array.isArray(value)) {
        return value.filter(
            (entry): entry is [string, T] =>
                Array.isArray(entry) && typeof entry[0] === "string",
        );
    }
    if (value && typeof value === "object") {
        return Object.entries(value as Record<string, T>);
    }
    return [];
}

function mapLike<T>(value: unknown): Map<string, T> {
    return new Map<string, T>(mapEntries<T>(value));
}

export function normalizeForDefinition(forDefinition: ForDefinition): ForDefinition {
    const innerScope = (forDefinition.innerScope ?? {}) as Partial<ParsedScope>;
    const nestedForDefinitions = new Map<string, ForDefinition>();
    for (const [id, nestedFor] of mapEntries<ForDefinition>(
        innerScope.forDefinitions,
    )) {
        nestedForDefinitions.set(id, normalizeForDefinition(nestedFor));
    }

    const normalizedInnerScope: ParsedScope = {
        ...innerScope,
        definitions: mapLike<PluginDefinition>(innerScope.definitions),
        contentDefinitions: mapLike<ContentDefinition>(
            innerScope.contentDefinitions,
        ),
        forDefinitions: nestedForDefinitions,
    };
    if (Array.isArray(innerScope.uses)) {
        normalizedInnerScope.uses = innerScope.uses;
    }
    if (Array.isArray(innerScope.blocks)) {
        normalizedInnerScope.blocks = innerScope.blocks;
    }

    return {
        ...forDefinition,
        innerScope: normalizedInnerScope,
    };
}

export function normalizeForDefinitions(
    forDefinitions: unknown,
): Map<string, ForDefinition> {
    const normalized = new Map<string, ForDefinition>();
    for (const [id, forDefinition] of mapEntries<ForDefinition>(forDefinitions)) {
        normalized.set(
            id,
            normalizeForDefinition({
                ...forDefinition,
                id: forDefinition.id || id,
            }),
        );
    }
    return normalized;
}
